import { CurrencyPipe } from '@angular/common';
import {
  ACL_WTAX_LINE_COLUMNS,
  CUSTOMER_STATUS,
  EINVOICE_AP_TINTYPE,
  EINVOICE_TINTYPE,
  EInvoiceTransactionType,
  EInvoiceValidationControl
} from '@core';
import type { ACLCanType } from '@delon/acl';
import { CurrencyPricePipe } from '@delon/util';
import { Column } from 'devextreme/ui/data_grid';
import { TransactionEmailStatus } from '@shared-feature/components/email-status-modal/email-status-modal.component';

const birClassificationLookup = {
  dataSource: [
    { value: 1, text: 'Non individual' },
    { value: 2, text: 'Individual' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};
const IEStatusLookup = {
  dataSource: [
    { value: 'Submitted', text: 'Submitted' },
    { value: 'Valid', text: 'Valid' },
    { value: 'Invalid', text: 'Invalid' },
    { value: 'Cancelled', text: 'Cancelled' },
    { value: null, text: 'Pending' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};
const birCategoryLookup = {
  dataSource: [
    { value: 1, text: 'Private' },
    { value: 2, text: 'Government' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const saleStatusLookup = {
  dataSource: [
    { value: 'Unpaid', text: 'Unpaid' },
    { value: 'Fully Paid', text: 'Fully Paid' },
    { value: 'Partially Paid', text: 'Partially Paid' },
    { value: 'Cancelled', text: 'Cancelled' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

export const activityStatusLookup = {
  dataSource: [
    { value: 0, text: 'ACTIVE' },
    { value: 1, text: 'INACTIVE' },
    { value: 2, text: 'SUSPENDED' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const salesParkAndPostStatusLookup = {
  dataSource: [
    { value: 'Approved', text: 'Approved' },
    { value: 'Draft', text: 'Draft' },
    { value: 'Waiting for Approval', text: 'Waiting for Approval' },
    { value: 'Fully Paid', text: 'Fully Paid' },
    { value: 'Partially Paid', text: 'Partially Paid' },
    { value: 'Cancelled', text: 'Cancelled' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const sourceLookup = {
  dataSource: [
    { value: 0, text: 'Internal' },
    { value: 1, text: 'External' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const customerStatusLookup = {
  dataSource: CUSTOMER_STATUS,
  valueExpr: 'value',
  displayExpr: 'label'
};

const transferStatusLookup = {
  dataSource: [
    { value: '1', text: 'PARTIALLY TRANSFERRED' },
    { value: '2', text: 'FULLY TRANSFERRED' },
    { value: 'cancelled', text: 'CANCELLED' }, // Represents isCancelled: true
    { value: 'closed', text: 'CLOSED' } // Represents isClosed: true
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const eInvoiceValidationLookup = {
  dataSource: [
    {
      value: EInvoiceValidationControl.FollowSettings,
      label: 'Follow e-Invoice Settings'
    },
    {
      value: EInvoiceValidationControl.FullCompliance,
      label: 'Full Validation'
    },
    {
      value: EInvoiceValidationControl.DeferredCompliance,
      label: 'Deferred Validation'
    },
    { value: EInvoiceValidationControl.NonCompliance, label: 'Non-Validation' }
  ],
  valueExpr: 'value',
  displayExpr: 'label'
};
const EInvoiceTransactionTypeLookup = {
  dataSource: [
    {
      value: EInvoiceTransactionType.NA,
      label: ''
    },
    {
      value: EInvoiceTransactionType.Agent,
      label: 'Agents, dealers, distributors, etc'
    },
    {
      value: EInvoiceTransactionType.ForeignSuppliers,
      label: 'Foreign purchases (foreign suppliers)'
    },
    {
      value: EInvoiceTransactionType.RecipientOfTheDistribution,
      label: 'Profit distribution (recipients)'
    },
    {
      value: EInvoiceTransactionType.Merchant,
      label: 'E-commerce transactions (merchant/service providers)'
    },
    {
      value: EInvoiceTransactionType.RecipientOfThePayout,
      label: 'Pay-Out to betting and gaming (winners)'
    },
    {
      value: EInvoiceTransactionType.IndividualTaxpayer,
      label: 'Individual taxpayer providing goods or services'
    },
    {
      value: EInvoiceTransactionType.RecipientOfTheInterestPayment,
      label: 'Interest payment (recipients)'
    },
    { value: EInvoiceTransactionType.All, label: 'All' }
  ],
  valueExpr: 'value',
  displayExpr: 'label'
};
const idTypeLookup = {
  dataSource: EINVOICE_TINTYPE,
  valueExpr: 'value',
  displayExpr: 'label'
};
const apIdTypeLookup = {
  dataSource: EINVOICE_AP_TINTYPE,
  valueExpr: 'value',
  displayExpr: 'label'
};
const refundStatusLookup = {
  dataSource: [
    { value: '1', text: 'PARTIALLY REFUNDED' },
    { value: '2', text: 'FULLY REFUNDED' },
    { value: 'cancelled', text: 'CANCELLED' } // Represents isCancelled: true
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const waCategoryLookup = {
  dataSource: [
    { value: 1, text: 'Private' },
    { value: 2, text: 'Government' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const taxPayerTypeLookup = {
  dataSource: [
    { value: 0, text: 'Non' },
    { value: 1, text: 'VAT Registered' },
    { value: 2, text: 'Non-VAT Registered' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const emailStatusLookup = {
  dataSource: [
    { value: TransactionEmailStatus.SENT, text: 'Sent' },
    { value: TransactionEmailStatus.DELIVERED, text: 'Delivered' },
    { value: TransactionEmailStatus.FAILED, text: 'Failed' },
    { value: TransactionEmailStatus.OPENED, text: 'Opened' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const documentStatusLookup = {
  dataSource: [
    { value: '0', text: 'Draft' },
    { value: '1', text: 'Waiting for Approval' },
    { value: '2', text: 'Approved' }
  ],
  valueExpr: 'value',
  displayExpr: 'text'
};

const filterConfig = {
  allowFiltering: true,
  allowHeaderFiltering: true,
  allowSorting: true,
  allowGrouping: true
};
export const dxCheckbox = (): Column => ({
  caption: '',
  dataField: 'id',
  cellTemplate: 'dxCheckboxTpl',
  headerCellTemplate: 'dxHeaderCheckboxTpl',
  width: 55,
  allowFiltering: false,
  allowHiding: false,
  allowSearch: false,
  allowExporting: false,
  allowSorting: false,
  allowHeaderFiltering: false,
  allowResizing: false,
  allowReordering: false,
  fixed: true
});
export const dxEmailStatus = (key?: string): Column => ({
  caption: 'Email Status',
  dataField: key || 'emailStatus',
  cellTemplate: 'dxEmailStatusTpl',
  width: 150,
  lookup: emailStatusLookup,
  calculateCellValue: (rowData: any) => {
    return rowData?.emailStatus?.toString();
  },
  ...filterConfig
});
export const dxDate = (key?: string): Column => ({
  caption: 'Date',
  dataField: key || 'docDate',
  cellTemplate: 'dxDateTpl',
  dataType: 'date',
  width: 150,
  ...filterConfig,
  allowHiding: false
});

export const dxTotalDebit = (
  pipe: CurrencyPricePipe,
  key?: string
): Column => ({
  caption: 'Total Debit',
  dataField: key || 'totalDebit',
  width: 150,
  calculateCellValue: (rowData: any) =>
    pipe.transform(rowData?.[key || 'totalDebit']),
  ...filterConfig
});

export const dxTotalCredit = (
  pipe: CurrencyPricePipe,
  key?: string
): Column => ({
  caption: 'Total Credit',
  dataField: key || 'totalCredit',
  width: 150,
  calculateCellValue: (rowData: any) =>
    pipe.transform(rowData?.[key || 'totalCredit']),
  ...filterConfig
});

export const dxPostDate = (key?: string): Column => ({
  caption: 'Post Date',
  dataField: key || 'postDate',
  dataType: 'date',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxDueDate = (key?: string): Column => ({
  caption: 'Due Date',
  dataField: key || 'dueDate',
  dataType: 'date',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxRequiredDate = (key?: string): Column => ({
  caption: 'Required Date',
  dataField: key || 'requireDate',
  dataType: 'date',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxIdType = (key?: string): Column | any => ({
  caption: 'Id Type',
  dataField: key || 'tinType',
  cellTemplate: 'dxIdTypeTpl',
  width: 200,
  lookup: idTypeLookup,
  visible: false,
  ...filterConfig,
  country: 'MY'
});
export const dxApIdType = (key?: string): Column | any => ({
  caption: 'Id Type',
  dataField: key || 'tinType',
  cellTemplate: 'dxIdApTypeTpl',
  width: 200,
  lookup: apIdTypeLookup,
  visible: false,
  ...filterConfig,
  country: 'MY'
});
export const dxDocCode = (): Column => ({
  caption: 'Doc #',
  dataField: 'docCode',
  cellTemplate: 'dxDocCodeTpl',
  width: 150,
  ...filterConfig
});

export const dxCompanyName = (): Column => ({
  caption: 'Company Name',
  dataField: 'companyName',
  width: 250,
  ...filterConfig
});

export const dxWACategory = (): Column => ({
  caption: 'Category',
  dataField: 'waCategory',
  cellTemplate: 'dxWACategoryTpl',
  lookup: waCategoryLookup,
  width: 250,
  ...filterConfig
});
export const dxBranchCode = (): Column => ({
  caption: 'Branch #',
  dataField: 'tinBranchCode',
  width: 250,
  ...filterConfig
});
export const dxIsAR = (): Column => ({
  caption: 'Customer',
  dataField: 'isAR',
  dataType: 'boolean',
  width: 180,
  visible: false,
  ...filterConfig
});

export const dxIsAP = (): Column => ({
  caption: 'Supplier',
  dataField: 'isAP',
  width: 180,
  visible: false,
  dataType: 'boolean',
  ...filterConfig
});

export const dxTaxPayerType = (): Column => ({
  caption: 'Taxpayer Type',
  dataField: 'taxpayerType',
  cellTemplate: 'dxTaxPayerTypeTpl',
  lookup: taxPayerTypeLookup,
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxDistrict = (): Column => ({
  caption: 'District',
  dataField: 'district',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxDocumentType = (): Column => ({
  caption: 'Document Type',
  dataField: 'documentType',
  cellTemplate: 'dxDocumentTypeTpl',
  width: 200,
  ...filterConfig
});

export const dxIn = (pipe: CurrencyPricePipe): Column => ({
  caption: 'In',
  dataField: 'in',
  dataType: 'number',
  calculateCellValue: (rowData: any) => pipe.transform(rowData?.in),
  width: 250,
  ...filterConfig
});

export const dxOut = (pipe: CurrencyPricePipe): Column => ({
  caption: 'Out',
  dataField: 'out',
  dataType: 'number',
  calculateCellValue: (rowData: any) => pipe.transform(rowData?.out),
  width: 250,
  ...filterConfig
});

export const dxTransaction = (): Column => ({
  caption: 'Transaction',
  dataField: 'docType',
  cellTemplate: 'dxTransactionTpl',
  width: 150,
  ...filterConfig
});

export const dxSupplier = (key?: string): Column => ({
  caption: 'Supplier #',
  dataField: key || 'supplier.code',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxPurchaser = (key?: string): Column => ({
  caption: 'Purchaser',
  dataField: key || 'purchaser.code',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxPayBy = (key?: string): Column => ({
  caption: 'Payment by',
  dataField: key || 'account.name',
  width: 150,
  ...filterConfig
});

export const dxTo = (key?: string): Column => ({
  caption: 'To',
  dataField: key || 'customerName',
  width: 250,
  ...filterConfig
});
export const dxPayTo = (key?: string): Column => ({
  caption: 'Pay to',
  dataField: key || 'payTo',
  width: 250,
  ...filterConfig
});

export const dxPaymentTo = (key?: string): Column => ({
  caption: 'Payment To',
  dataField: key || 'account.name',
  width: 200,
  ...filterConfig
});

export const dxFrom = (key?: string): Column => ({
  caption: 'From',
  dataField: key || 'customerName',
  width: 250,
  ...filterConfig
});

export const dxBankCharges = (pipe: CurrencyPricePipe, key?: any): Column => ({
  caption: 'Bank Charges',
  dataField: key || 'bankChargesAmount',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    pipe.transform(rowData?.[key || 'bankChargesAmount']),
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxCustomer = (): Column => ({
  caption: 'Customer #',
  dataField: 'customerCode',
  width: 200,
  ...filterConfig
});

export const dxStatus = (parkAndPost: boolean): Column => ({
  caption: 'Status',
  dataField: 'status',
  cellTemplate: 'dxStatusTpl',
  width: 200,
  calculateCellValue: (rowData: any) => {
    if (parkAndPost && rowData.documentStatus < 2) {
      return rowData.documentStatus === 0 ? 'Draft' : 'Waiting for Approval';
    }
    return rowData?.status;
  },
  lookup: parkAndPost ? salesParkAndPostStatusLookup : saleStatusLookup,
  calculateFilterExpression: (
    filterValue: any,
    selectedFilterOperation: string | null
  ) => {
    // Map filter values to server-known status values
    let mappedValue = filterValue;
    let column = 'status';

    if (parkAndPost) {
      // For park and post mode, map display values to server values
      switch (filterValue) {
        case 'Draft':
          mappedValue = 0;
          column = 'documentStatus';
          break;
        case 'Waiting for Approval':
          mappedValue = 1;
          column = 'documentStatus';
          break;
        case 'Approved':
          mappedValue = 2;
          column = 'documentStatus';
          return [
            [column, selectedFilterOperation || '=', mappedValue],
            ['status', '=', 'Unpaid']
          ];
        case 'Fully Paid':
          mappedValue = 'Fully Paid';
          column = 'status';
          break;
        case 'Partially Paid':
          mappedValue = 'Partially Paid';
          column = 'status';
          break;
        case 'Cancelled':
          mappedValue = 'Cancelled';
          column = 'status';
          break;
        default:
          mappedValue = filterValue;
      }
    } else {
      column = 'status';
      // For regular sales mode, map display values to server values
      switch (filterValue) {
        case 'Unpaid':
          mappedValue = 'Unpaid';
          break;
        case 'Fully Paid':
          mappedValue = 'Fully Paid';
          break;
        case 'Partially Paid':
          mappedValue = 'Partially Paid';
          break;
        case 'Cancelled':
          mappedValue = 'Cancelled';
          break;
        default:
          mappedValue = filterValue;
      }
    }

    // Return the filter expression with mapped value
    return [[column, selectedFilterOperation || '=', mappedValue]];
  },
  ...filterConfig
});
export const dxCustomerStatus = (): Column => ({
  caption: 'Status',
  dataField: 'status',
  cellTemplate: 'dxCustomerStatusTpl',
  lookup: customerStatusLookup,
  width: 200,
  ...filterConfig
});

export const dxDescription = (): Column => ({
  caption: 'Desc.',
  dataField: 'description',
  width: 300,
  ...filterConfig
});

export const dxAgent = (key?: string): Column => ({
  caption: 'Agent',
  dataField: key || 'agentCode',
  width: 100,
  ...filterConfig
});

export const dxEInvStatus = (): Column => ({
  caption: 'e-Inv Status',
  dataField: 'eInvoiceStatus',
  cellTemplate: 'dxEInvStatusTpl',
  width: 150,
  // Mirror the display logic in dx-einv-status-tpl so sort/export/filter
  // align with what the user actually sees in the cell.
  calculateCellValue: (rowData: any) => {
    const status = rowData?.consolidatedUIN
      ? rowData?.consolidatedStatus
      : rowData?.eInvoiceStatus;
    return status || 'Pending';
  },
  calculateFilterExpression: (
    filterValue: any,
    selectedFilterOperation: string | null
  ) => {
    const op = selectedFilterOperation || 'contains';
    return [
      [
        ['consolidatedUIN', '<>', null],
        'and',
        ['consolidatedStatus', op, filterValue]
      ],
      'or',
      [
        ['consolidatedUIN', '=', null],
        'and',
        ['eInvoiceStatus', op, filterValue]
      ]
    ];
  },
  ...filterConfig,
  visible: true
});

export const dxSupplierInvNo = (): Column => ({
  caption: ' Supplier INV #',
  dataField: 'supplierInvNo',
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxAmount = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false,
  key: string = 'netTotalAmount'
): Column => ({
  caption: 'Amount',
  dataField: key,
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData[key]),
  width: 150,
  ...filterConfig
});

export const dxOutStandingAmount = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false
): Column => ({
  caption: 'Outstanding Amount',
  dataField: 'outstandingAmount',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData?.outstandingAmount),
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxSellingPrice = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false,
  key: string = 'sellingPrice'
): Column => ({
  caption: 'Selling Price',
  dataField: key,
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData[key]),
  width: 150,
  ...filterConfig
});

export const dxTaxAmount = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false
): Column => ({
  caption: 'Tax Amount',
  dataField: 'taxTotalAmount',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData?.taxTotalAmount),
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxWTaxAmount = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false
): Column | any => ({
  caption: 'WTax Amount',
  dataField: 'wTaxTotalAmount',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData?.wTaxTotalAmount),
  width: 150,
  ...filterConfig,
  visible: false,
  acl: ACL_WTAX_LINE_COLUMNS
});

export const dxTransferStatus = (): Column => ({
  caption: 'Transfer Status',
  dataField: 'transferStatus',
  cellTemplate: 'dxTransferStatusTpl',
  width: 160,
  lookup: transferStatusLookup,
  calculateCellValue: (rowData: any) => {
    return rowData?.transferStatus?.toString();
  },
  calculateFilterExpression: (
    filterValue: any,
    selectedFilterOperation: string | null
  ) => {
    let mappedValue = filterValue;
    let column = 'transferStatus';
    switch (filterValue) {
      case 'cancelled':
        mappedValue = true;
        column = 'isCancelled';
        break;
      case 'closed':
        mappedValue = true;
        column = 'isClosed';
        break;
      default:
        mappedValue = filterValue;
        break;
    }
    return [[column, selectedFilterOperation || '=', mappedValue]];
  },
  ...filterConfig
});

export const dxRefundStatus = (): Column => ({
  caption: 'Transfer Status',
  dataField: 'refundStatus',
  cellTemplate: 'dxRefundStatusTpl',
  width: 200,
  lookup: refundStatusLookup,
  calculateCellValue: (rowData: any) => {
    if (rowData.isCancelled === true) {
      return 'cancelled';
    }
    return rowData?.refundStatus?.toString();
  },
  ...filterConfig
});

export const dxDocumentStatus = (): Column => ({
  caption: 'Status',
  dataField: 'documentStatus',
  cellTemplate: 'dxDocumentStatusTpl',
  width: 200,
  lookup: documentStatusLookup,
  ...filterConfig
});

export const dxAction = (): Column => ({
  caption: 'Action',
  dataField: 'match',
  cellTemplate: 'dxActionTpl',
  allowGrouping: false,
  allowHiding: false,
  allowFiltering: false,
  minWidth: 150,
  allowHeaderFiltering: false,
  allowSorting: false,
  allowResizing: true
});

// Hidden Columns

export const dxCustomerArea = (key?: string): Column => ({
  caption: 'Customer Area',
  dataField: key || 'customerArea.code',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxCustomerCategory = (key?: string): Column => ({
  caption: 'Customer Category',
  dataField: key || 'customerCategory.code',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxSupplierArea = (key?: string): Column => ({
  caption: 'Supplier Area',
  dataField: key || 'supplierArea.code',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxSupplierCategory = (key?: string): Column => ({
  caption: 'Supplier Category',
  dataField: key || 'supplierCategory.code',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxReferenceNo = (key?: string): Column => ({
  caption: 'Ref No',
  dataField: key || 'referenceNo',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxTerm = (key?: string): Column => ({
  caption: 'Term',
  dataField: key || 'term.code',
  width: 100,
  visible: false,
  ...filterConfig
});

export const dxPostToAP = (key?: string): Column => ({
  caption: 'Is Post To AP',
  dataField: key || 'isPostToAP',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxBillingAddress = (key?: string): Column => ({
  caption: 'Billing Address',
  dataField: key || 'billingAddress',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxAddress = (key?: string): Column => ({
  caption: 'Address',
  dataField: key || 'address',
  width: 250,
  ...filterConfig,
  visible: false
});
export const dxAddress1 = (key?: string): Column => ({
  caption: 'Address 1',
  dataField: key || 'address1',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxAddress2 = (key?: string): Column => ({
  caption: 'Address 2',
  dataField: key || 'address2',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxAddress3 = (key?: string): Column => ({
  caption: 'Address 3',
  dataField: key || 'address3',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxAddress4 = (key?: string): Column => ({
  caption: 'Address 4',
  dataField: key || 'address4',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxValidity = (key?: string): Column => ({
  caption: 'Validity',
  dataField: key || 'validity',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxValidationControl = (key?: string): Column | any => ({
  caption: 'E-Invoice Validation Control',
  dataField: key || 'validationControl',
  width: 250,
  cellTemplate: 'dxEInvoiceValidationControlTpl',
  lookup: eInvoiceValidationLookup,
  ...filterConfig,
  visible: false,
  country: 'MY'
});

export const dxValidateOn = (key?: string): Column & { country?: string } => ({
  caption: 'Validate On',
  dataField: key || 'validateOn',
  width: 180,
  dataType: 'datetime',
  ...filterConfig,
  visible: false,
  country: 'MY'
});

export const dxDeliveryTerm = (key?: string): Column => ({
  caption: 'Delivery Term',
  dataField: key || 'deliveryTerm',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxTinNo = (key?: string): Column => ({
  caption: 'TIN No.',
  dataField: key || 'tinNumber',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxRegistrationNo = (key?: string): Column => ({
  caption: 'Reg No.',
  dataField: key || 'registrationNo',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxName = (key?: string): Column => ({
  caption: 'Name',
  dataField: key || 'name',
  width: 300,
  ...filterConfig
});

export const dxName2 = (key?: string): Column => ({
  caption: 'Customer Name 2',
  dataField: key || 'name2',
  width: 200,
  visible: false,
  ...filterConfig
});
export const dxContactPerson = (key?: string): Column => ({
  caption: 'Contact Person',
  dataField: key || 'contactPerson',
  width: 300,
  visible: false,
  ...filterConfig
});

export const dxWebsite = (key?: string): Column => ({
  caption: 'Website',
  dataField: key || 'website',
  width: 300,
  visible: false,
  ...filterConfig
});

export const dxPhone1 = (key?: string): Column => ({
  caption: 'Phone 1',
  dataField: key || 'phoneNo1',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxPhone2 = (key?: string): Column => ({
  caption: 'Phone 2',
  dataField: key || 'phoneNo2',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxFax1 = (key?: string): Column => ({
  caption: 'Fax 1',
  dataField: key || 'faxNo1',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxFax2 = (key?: string): Column => ({
  caption: 'Fax 2',
  dataField: key || 'faxNo2',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxBusinessNature = (key?: string): Column => ({
  caption: 'Business Nature',
  dataField: key || 'bizNature',
  width: 200,
  visible: false,
  ...filterConfig
});
export const dxDeferredVat = (key?: string): Column | any => ({
  caption: 'Deferred VAT',
  dataField: key || 'isDeferredTax',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'PH'
});

export const dxSaleTaxRegNo = (key?: string): Column | any => ({
  caption: 'Sale Tax Reg No',
  dataField: key || 'salesTaxRegNo',
  width: 300,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxServiceTaxRegNo = (key?: string): Column | any => ({
  caption: 'Service Tax Reg No',
  dataField: key || 'serviceTaxRegNo',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxTourismTaxRegNo = (key?: string): Column | any => ({
  caption: 'Tourism Tax Reg No',
  dataField: key || 'tourismTaxRegNo',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxFrequencyOfBilling = (key?: string): Column | any => ({
  caption: 'Frequency Of Billing',
  dataField: key || 'frequencyOfBilling',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});
export const dxMSIC = (key?: string): Column | any => ({
  caption: 'MSIC',
  dataField: key || 'msicCode',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxBillingPeriodFrom = (key?: string): Column | any => ({
  caption: 'Billing Period From',
  dataField: key || 'billingPeriodFrom',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxBillingPeriodTo = (key?: string): Column | any => ({
  caption: 'Billing Period To',
  dataField: key || 'billingPeriodTo',
  width: 200,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxCreditLimitValue = (key?: string): Column | any => ({
  caption: 'Credit Limit',
  dataField: key || 'creditLimitValue',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxCreditLimitValidUntil = (key?: string): Column | any => ({
  caption: 'Valid Until',
  dataField: key || 'creditLimitValidUntil',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxCreditLimitExceeded = (key?: string): Column | any => ({
  caption: 'When the credit limit is exceeded',
  dataField: key || 'creditLimitExceededBehavior',
  width: 200,
  visible: false,
  ...filterConfig
});
export const dxApplyInTheseTransactions = (key?: string): Column | any => ({
  caption: 'Apply in these transactions',
  dataField: key || 'creditLimitApplyForTransactions',
  width: 200,
  visible: false,
  ...filterConfig
});

export const dxPeppolId = (key?: string): Column | any => ({
  caption: 'Peppol ID',
  dataField: key || 'peppolId',
  width: 300,
  visible: false,
  ...filterConfig,
  country: 'MY'
});

export const dxCode = (key?: string): Column => ({
  caption: 'Code',
  dataField: key || 'code',
  width: 200,
  ...filterConfig
});
export const dxPostToAR = (key?: string): Column => ({
  caption: 'Post To AR',
  dataField: key || 'isPostToAR',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxDepositTo = (key?: string): Column => ({
  caption: 'Deposit To',
  dataField: key || 'accountName',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxCurrency = (key?: string): Column => ({
  caption: 'Currency',
  dataField: key || 'currency.code',
  width: 100,
  ...filterConfig,
  visible: false
});

export const dxSupplierCn = (key?: string): Column => ({
  caption: 'Supplier CN #',
  dataField: key || 'supplierCNNo',
  width: 100,
  ...filterConfig,
  visible: false
});
export const dxSupplierDo = (key?: string): Column => ({
  caption: 'Supplier Do #',
  dataField: key || 'supplierDONo',
  width: 100,
  ...filterConfig,
  visible: false
});

export const dxCurrencyRate = (pipe: CurrencyPipe): Column => ({
  caption: 'Currency Rate',
  dataField: 'currencyRate',
  calculateCellValue: (rowData: any) =>
    pipe.transform(rowData?.currencyRate, '', '', '1.8-8'),
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxSource = (key?: string): Column => ({
  caption: 'Source',
  dataField: key || 'source',
  width: 150,
  cellTemplate: 'dxSourceTpl',
  lookup: sourceLookup,
  ...filterConfig,
  visible: true
});
export const dxTransactionPeriod = (key?: string): Column => ({
  caption: 'Transaction Period',
  dataField: key || 'periodRange',
  width: 300,
  ...filterConfig,
  visible: true
});

export const dxPaymentBy = (key?: string): Column => ({
  caption: 'Payment By',
  dataField: key || 'accountCode',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxRefundFrom = (key?: string): Column => ({
  caption: 'Refunded From',
  dataField: key || 'refundFrom',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxTransferTo = (key?: string): Column => ({
  caption: 'Transfer To',
  dataField: key || 'transferTo',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxProject = (key?: string): Column => ({
  caption: 'Project',
  dataField: key || 'projectCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxLocation = (key?: string): Column => ({
  caption: 'Location',
  dataField: key || 'stockLocationCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxZipCode = (key?: string): Column | any => ({
  caption: 'Zip Code',
  dataField: key || 'zipCode',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'MY'
});

export const dxCountry = (key?: string): Column | any => ({
  caption: 'Country',
  dataField: key || 'country',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'MY'
});
export const dxState = (key?: string): Column | any => ({
  caption: 'State',
  dataField: key || 'state',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'MY'
});

export const dxCity = (key?: string): Column | any => ({
  caption: 'City',
  dataField: key || 'city',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'MY'
});

export const dxPhone = (key?: string): Column => ({
  caption: 'Phone',
  dataField: key || 'customerPhone',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxEmail = (key?: string): Column => ({
  caption: 'Email',
  dataField: key || 'email',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxIdentificationNo = (key?: string): Column => ({
  caption: 'Identification No',
  dataField: key || 'registrationNo',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxBirCategory = (key?: string): Column | any => ({
  caption: 'BIR Category',
  dataField: key || 'birCategory',
  width: 150,
  lookup: birCategoryLookup,
  ...filterConfig,
  visible: false,
  country: 'PH'
});

export const dxBirInfo = (key?: string): Column | any => ({
  caption: 'BIR Info',
  dataField: key || 'birInfo',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'PH'
});

export const dxBirClassification = (key?: string): Column | any => ({
  caption: 'BIR Classification',
  dataField: key || 'birClassification',
  width: 150,
  lookup: birClassificationLookup,
  ...filterConfig,
  visible: false,
  country: 'PH'
});

export const dxTaxInclusive = (key?: string): Column => ({
  caption: 'Tax Inclusive',
  dataField: key || 'isTaxInclusive',
  width: 100,
  ...filterConfig,
  visible: false
});

export const dxAttachment = (key?: string): Column => ({
  caption: 'Attachment',
  dataField: key || 'hasAttachment',
  dataType: 'boolean',
  cellTemplate: 'dxAttachmentTpl',
  calculateCellValue: (rowData: any) => {
    const raw = rowData?.[key || 'hasAttachment'];
    return raw === true || raw === 1;
  },
  width: 100,
  ...filterConfig,
  allowSorting: false, // Sorting not supported for computed properties
  allowGrouping: false, // Grouping not supported for computed properties
  visible: false
});

export const dxTaxDebit = (pipe: CurrencyPricePipe, key?: string): Column => ({
  caption: 'Tax Debit',
  dataField: key || 'totalTaxDebit',
  dataType: 'number',
  calculateCellValue: (rowData: any) => pipe.transform(rowData?.totalTaxDebit),
  width: 100,
  ...filterConfig,
  visible: false
});

export const dxTaxCredit = (pipe: CurrencyPricePipe, key?: string): Column => ({
  caption: 'Tax Credit',
  dataField: key || 'totalTaxCredit',
  calculateCellValue: (rowData: any) => pipe.transform(rowData?.totalTaxCredit),
  width: 100,
  dataType: 'number',
  ...filterConfig,
  visible: false
});

export const dxCost = (
  pipe: CurrencyPricePipe,
  includeTax?: boolean
): Column => ({
  caption: 'Cost',
  dataField: 'cost',
  dataType: 'number',
  width: 100,
  calculateCellValue: (data: any) => {
    let value;
    if (includeTax) {
      value = data?.costNetAmountLocal;
    } else {
      value = data?.costTaxExclusiveAmountLocal;
    }
    return pipe.transform(value);
  },
  ...filterConfig
});

export const dxPrice = (
  pipe: CurrencyPricePipe,
  includeTax?: boolean
): Column => ({
  caption: 'Price',
  dataField: 'price',
  dataType: 'number',
  width: 100,
  calculateCellValue: (data: any) => {
    let value;
    if (includeTax) {
      value = data?.priceNetAmountLocal;
    } else {
      value = data?.priceTaxExclusiveAmountLocal;
    }

    return pipe.transform(value);
  },
  ...filterConfig
});
export const dxTransferFrom = (key?: string): Column => ({
  caption: 'Transfer From',
  dataField: key || 'transferFrom',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxTransactionType = (key?: string): Column => ({
  caption: 'TransactionType',
  dataField: key || 'transactionType',
  width: 150,
  lookup: EInvoiceTransactionTypeLookup,
  ...filterConfig,
  visible: false
});

export const dxIsSelfBilled = (key?: string): Column => ({
  caption: 'Self Billed',
  dataField: key || 'isSelfBilled',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxUIN = (key?: string): Column => ({
  caption: 'UIN',
  dataField: key || 'uin',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxReference1 = (key?: string): Column => ({
  caption: 'Reference 1',
  dataField: key || 'ref1',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxReference2 = (key?: string): Column => ({
  caption: 'Reference 2',
  dataField: key || 'ref2',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxReference3 = (key?: string): Column => ({
  caption: 'Reference 3',
  dataField: key || 'ref3',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxReference4 = (key?: string): Column => ({
  caption: 'Reference 4',
  dataField: key || 'ref4',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxReference5 = (key?: string): Column => ({
  caption: 'Reference 5',
  dataField: key || 'ref5',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxRemark1 = (key?: string): Column => ({
  caption: 'Remark 1',
  dataField: key || 'remark1',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxRemark2 = (key?: string): Column => ({
  caption: 'Remark 2',
  dataField: key || 'remark2',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxRemark3 = (key?: string): Column => ({
  caption: 'Remark 3',
  dataField: key || 'remark3',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxRemark4 = (key?: string): Column => ({
  caption: 'Remark 4',
  dataField: key || 'remark4',
  width: 150,
  ...filterConfig,
  visible: false
});
export const dxRemark5 = (key?: string): Column => ({
  caption: 'Remark 5',
  dataField: key || 'remark5',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxNotes = (key?: string): Column => ({
  caption: 'Notes',
  dataField: key || 'notes',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxBankAccount = (key?: string): Column => ({
  caption: 'Bank Account',
  dataField: key || 'bankAccount.fullDisplay',
  cellTemplate: 'dxBankAccountTpl',
  width: 250,
  ...filterConfig
});
export const dxAreaCode = (key?: string): Column => ({
  caption: 'Area',
  dataField: key || 'areaCode',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxCategoryCode = (key?: string): Column => ({
  caption: 'Category',
  dataField: key || 'categoryCode',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxControlAccountCode = (key?: string): Column => ({
  caption: 'Control Acc.',
  dataField: key || 'controlAccountCode',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxBalance = (pipe: CurrencyPricePipe, key?: string): Column => ({
  caption: 'Balance',
  dataField: key || 'balance',
  dataType: 'number',
  customizeText: (cellInfo: any) =>
    pipe.transform(cellInfo.value),
  width: 250,
  ...filterConfig
});

export const dxClearedBalance = (key?: string): Column => ({
  caption: 'Cleared Balance',
  dataField: key || 'notes',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxDifference = (key?: string): Column => ({
  caption: 'Difference',
  dataField: key || 'notes',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxAccountBalance = (key?: string): Column => ({
  caption: 'Account Balance',
  dataField: key || 'notes',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxUncreditedFunds = (key?: string): Column => ({
  caption: 'Uncredited Funds',
  dataField: key || 'notes',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxUnpresentedFunds = (key?: string): Column => ({
  caption: 'Unpresented Funds',
  dataField: key || 'notes',
  width: 250,
  visible: false,
  ...filterConfig
});

export const dxConsolidatedDocCode = (): Column => ({
  caption: 'Consolidated Doc #',
  dataField: 'consolidatedDocCode',
  cellTemplate: 'dxConsolidatedDocCodeTpl',
  width: 150,
  visible: false,
  ...filterConfig
});
export const dxBir2307Collected = (key?: string): Column | any => ({
  caption: 'BIR 2307 Collected',
  dataField: key || 'isBir2307Collected',
  dataType: 'boolean',
  calculateCellValue: (rowData: any) => {
    const raw = rowData?.[key || 'isBir2307Collected'];
    return raw === true || raw === 1;
  },
  calculateFilterExpression: (
    filterValue: any,
    selectedFilterOperation: string | null
  ) => {
    const column = key || 'isBir2307Collected';
    if (filterValue === true) {
      return [
        [column, selectedFilterOperation || '=', 1],
        'or',
        [column, selectedFilterOperation || '=', true]
      ];
    }
    // Treat everything else as unchecked: 0, false, null
    return [
      [column, selectedFilterOperation || '=', 0],
      'or',
      [column, selectedFilterOperation || '=', false],
      'or',
      [column, selectedFilterOperation || '=', null]
    ];
  },
  width: 120,
  ...filterConfig,
  visible: false,
  country: 'PH'
});

export const dxActive = (key?: string): Column => ({
  caption: 'Active',
  dataField: 'isActive',
  dataType: 'boolean',
  width: 150,
  ...filterConfig
});

export const dxCategory = (key?: string): Column => ({
  caption: 'Category',
  dataField: key || 'categoryCode',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxGroup = (key?: string): Column => ({
  caption: 'Group',
  dataField: key || 'groupCode',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxClass = (key?: string): Column => ({
  caption: 'Class',
  dataField: key || 'classCode',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxPurchasePrice = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false,
  key?: string
): Column => ({
  caption: 'Purchase Price',
  dataField: key || 'purchasePrice',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData[key || 'purchasePrice']),
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxAlternateName = (key?: string): Column => ({
  caption: 'Alternate Name',
  dataField: key || 'alternateName',
  width: 250,
  ...filterConfig,
  visible: false
});

export const dxListPrice = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false,
  key?: string
): Column => ({
  caption: 'List Price',
  dataField: key || 'listPrice',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData[key || 'listPrice']),
  width: 150,
  visible: false,
  ...filterConfig
});

export const dxStockControl = (key?: string): Column => ({
  caption: 'Stock Control',
  dataField: key || 'isStockControl',
  dataType: 'boolean',
  calculateCellValue: (rowData: any) => {
    const raw = rowData?.[key || 'isStockControl'];
    return raw === true || raw === 1;
  },
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxMinPrice = (
  pipe: CurrencyPricePipe,
  isProtected: boolean = false,
  key?: string
): Column => ({
  caption: 'Min Price',
  dataField: key || 'minPrice',
  dataType: 'number',
  calculateCellValue: (rowData: any) =>
    isProtected ? '*****' : pipe.transform(rowData[key || 'minPrice']),
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxStockLocation = (key?: string): Column => ({
  caption: 'Stock Location',
  dataField: key || 'locationCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxEInvValidationControl = (key?: string): Column => ({
  caption: 'E-Invoice Validation Control',
  dataField: key || 'validationControl',
  width: 250,
  cellTemplate: 'dxEInvoiceValidationControlTpl',
  lookup: eInvoiceValidationLookup,
  ...filterConfig,
  visible: false
});

export const dxAccountPreset = (key?: string): Column => ({
  caption: 'Account Preset',
  dataField: key || 'accountPresetCode',
  width: 200,
  ...filterConfig,
  visible: false
});

export const dxClassification = (key?: string): Column => ({
  caption: 'Classification',
  dataField: key || 'salesClassification',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxBasedUOM = (key?: string): Column => ({
  caption: 'Base UOM',
  dataField: key || 'baseUOM',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxDefaultInputTax = (key?: string): Column => ({
  caption: 'Default Input Tax',
  dataField: key || 'inputTaxCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxDefaultOutputTax = (key?: string): Column => ({
  caption: 'Default Output Tax',
  dataField: key || 'outputTaxCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxDiffultTarrifCode = (key?: string): Column => ({
  caption: 'Tariff Code',
  dataField: key || 'tariffCode',
  width: 150,
  ...filterConfig,
  visible: false
});

export const dxBir2307CollectedDate = (key?: string): Column | any => ({
  caption: 'BIR 2307 Collected Date',
  dataField: key || 'bir2307CollectedDate',
  dataType: 'date',
  width: 150,
  ...filterConfig,
  visible: false,
  country: 'PH',
  customizeText: (e: any) => (e && e.value ? e.valueText : '')
});

// Knockoff (Receive Payment) detail grid columns - used with DevExpressGridDetailsBase
export type KnockoffDxColumn = Column & { acl?: string | ACLCanType; docType?: string };

export const dxKnockoffAutoMatch = (): KnockoffDxColumn => ({
  caption: '',
  dataField: '_autoMatch',
  headerCellTemplate: 'dxKnockoffAutoMatchHeaderTpl',
  cellTemplate: 'dxKnockoffMatchSwitchTpl',
  width: 90,
  allowFiltering: false,
  allowSorting: false,
  allowHiding: false
});

export const dxKnockoffType = (): KnockoffDxColumn => ({
  caption: 'Type',
  dataField: 'docType',
  ...filterConfig
});

export const dxKnockoffDate = (): KnockoffDxColumn => ({
  caption: 'Date',
  dataField: 'docDate',
  cellTemplate: 'dxDateTpl',
  dataType: 'date',
  ...filterConfig
});

export const dxKnockoffDocCode = (): KnockoffDxColumn => ({
  caption: 'Doc #',
  dataField: 'docCode',
  cellTemplate: 'dxKnockoffDocCodeTpl',
  ...filterConfig
});

export const dxKnockoffDescription = (): KnockoffDxColumn => ({
  caption: 'Desc',
  dataField: 'description',
  ...filterConfig
});

export const dxKnockoffRefNo = (): KnockoffDxColumn => ({
  caption: 'Ref No.',
  dataField: 'referenceNo',
  ...filterConfig
});

export const dxKnockoffSupplierInvNo = (): KnockoffDxColumn => ({
  caption: 'Supplier INV #',
  dataField: 'supplierInvNo',
  ...filterConfig
});

export const dxKnockoffAmount = (): KnockoffDxColumn => ({
  caption: 'Amount',
  dataField: 'amount',
  cellTemplate: 'dxKnockoffAmountTpl',
  alignment: 'right',
  ...filterConfig
});

export const dxKnockoffOutstanding = (): KnockoffDxColumn => ({
  caption: 'Outstanding',
  dataField: '_outstanding',
  cellTemplate: 'dxKnockoffOutstandingTpl',
  alignment: 'right',
  ...filterConfig
});

export const dxKnockoffPayment = (): KnockoffDxColumn => ({
  caption: 'Payment',
  dataField: 'paymentAmount',
  cellTemplate: 'dxKnockoffPaymentTpl',
  alignment: 'right',
  ...filterConfig
});

export const dxKnockoffEInvStatus = (): KnockoffDxColumn => ({
  caption: 'E-Invoice',
  dataField: 'eInvoiceStatus',
  cellTemplate: 'dxKnockoffEInvStatusTpl',
  alignment: 'center',
  ...filterConfig,
  acl: 'einv.enabled'
});

export const dxKnockoffWTax = (): KnockoffDxColumn => ({
  caption: 'W/Tax',
  dataField: 'totalWTaxAmount',
  cellTemplate: 'dxKnockoffWTaxTpl',
  alignment: 'right',
  ...filterConfig,
  acl: ACL_WTAX_LINE_COLUMNS,
  docType: 'OR'
});

/** AP Supplier Knockoff: W/Tax column for PV doc type */
export const dxApKnockoffWTax = (): KnockoffDxColumn => ({
  caption: 'W/Tax',
  dataField: 'totalWTaxAmount',
  cellTemplate: 'dxApKnockoffWTaxTpl',
  alignment: 'right',
  ...filterConfig,
  acl: ACL_WTAX_LINE_COLUMNS,
  docType: 'PV'
});

export const dxKnockoffWVat = (): KnockoffDxColumn => ({
  caption: 'WVat',
  dataField: 'totalWVatAmount',
  cellTemplate: 'dxKnockoffWVatTpl',
  alignment: 'right',
  ...filterConfig,
  acl: 'country.PH',
  docType: 'OR'
});

export const dxKnockoffCurrencyRate = (): KnockoffDxColumn => ({
  caption: 'Currency Rate',
  dataField: 'currencyRate',
  dataType: 'number',
  format: { type: 'fixedPoint', precision: 8 },
  ...filterConfig,
  visible: false
});

export const dxKnockoffProject = (): KnockoffDxColumn => ({
  caption: 'Project',
  dataField: 'projectCode',
  ...filterConfig,
  visible: false
});

export const dxKnockoffStockLocation = (): KnockoffDxColumn => ({
  caption: 'Stock Location',
  dataField: 'locationCode',
  ...filterConfig,
  visible: false
});

export const dxKnockoffAgentCode = (): KnockoffDxColumn => ({
  caption: 'Agent Code',
  dataField: 'agentCode',
  ...filterConfig,
  visible: false
});

export const dxKnockoffAgentName = (): KnockoffDxColumn => ({
  caption: 'Agent Name',
  dataField: 'agentName',
  ...filterConfig,
  visible: false
});

export const dxKnockoffReference1 = (): KnockoffDxColumn => ({
  caption: 'Reference 1',
  dataField: 'reference1',
  ...filterConfig,
  visible: false
});
export const dxKnockoffReference2 = (): KnockoffDxColumn => ({
  caption: 'Reference 2',
  dataField: 'reference2',
  ...filterConfig,
  visible: false
});
export const dxKnockoffReference3 = (): KnockoffDxColumn => ({
  caption: 'Reference 3',
  dataField: 'reference3',
  ...filterConfig,
  visible: false
});
export const dxKnockoffReference4 = (): KnockoffDxColumn => ({
  caption: 'Reference 4',
  dataField: 'reference4',
  ...filterConfig,
  visible: false
});
export const dxKnockoffRemark1 = (): KnockoffDxColumn => ({
  caption: 'Remark 1',
  dataField: 'remark1',
  ...filterConfig,
  visible: false
});
export const dxKnockoffRemark2 = (): KnockoffDxColumn => ({
  caption: 'Remark 2',
  dataField: 'remark2',
  ...filterConfig,
  visible: false
});
export const dxKnockoffRemark3 = (): KnockoffDxColumn => ({
  caption: 'Remark 3',
  dataField: 'remark3',
  ...filterConfig,
  visible: false
});
export const dxKnockoffRemark4 = (): KnockoffDxColumn => ({
  caption: 'Remark 4',
  dataField: 'remark4',
  ...filterConfig,
  visible: false
});

export const saleHiddenColumns: Array<(key?: string) => Column> = [
  dxReferenceNo,
  dxBillingAddress,
  dxValidity,
  dxDeliveryTerm,
  dxCurrency,
  dxTransferTo,
  dxProject,
  dxLocation,
  dxZipCode,
  dxCountry,
  dxCity,
  dxTerm,
  dxState,
  dxPhone,
  dxEmail,
  dxTinNo,
  dxIdentificationNo,
  dxBirCategory,
  dxBirClassification,
  dxTaxInclusive,
  dxReference1,
  dxReference2,
  dxReference3,
  dxReference4,
  dxRemark1,
  dxRemark2,
  dxRemark3,
  dxNotes
];
